// Auth facade. Screens and api.ts use ONLY these functions; the strategy
// (authentik | local) is picked from the operating mode via the registry.

import { API_BASE, BUILD_MODE, runtimeFlags, type ErpMode } from '../env';
import { lockStore } from '../idle-lock';
import { getStrategy, strategyNameFor } from './registry';
import { sessionStore } from './session';
import { AuthError, AUTH_COPY, type AuthStrategyName, type LoginResult } from './types';
// Strategies self-register (blank-import equivalents):
import './authentik';
import { toLoginResult, type LocalSessionResponse } from './local';

export { sessionStore } from './session';
export { AuthError, AUTH_COPY, parseLocked } from './types';
export type { AuthStrategy, AuthStrategyName, LoginResult, SessionMembership, SessionTokens, SessionUser, AuthErrorCode } from './types';
export { registerStrategy, getStrategy, registeredStrategies, strategyNameFor } from './registry';
export { createEnrollment } from './enrollment';
export type { Enrollment } from './enrollment';
export type { OtpChannel, OtpChannelInfo, OtpClient } from './otp_exchange';

let runtimeMode: ErpMode = BUILD_MODE;

/** The server's mode (GET /me) overrides the build default once known. */
export function setRuntimeMode(mode: ErpMode): void {
  runtimeMode = mode;
}
export function getRuntimeMode(): ErpMode {
  return runtimeMode;
}

/**
 * Ask the server which mode it runs in BEFORE sign-in (the mode from GET /me is
 * only known after it). A node answers erp-core's
 * `GET /api/erp/auth/status` → `{mode:"node", …}`; the cloud does not mount
 * that route (404) and stays on the build default. So the one bundle in the
 * image signs in on a node too (e2e login-today-booking-1: a VITE_ERP_MODE=online
 * bundle served by a node tried the Authentik flow and 404'd). Never throws;
 * a network failure changes nothing.
 */
export async function probeServerMode(fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<ErpMode | null> {
  try {
    const res = await fetchImpl(`${API_BASE}/erp/auth/status`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const body = (await res.json()) as { mode?: unknown };
    if (body?.mode !== 'node') return null;
    runtimeMode = 'node';
    return 'node';
  } catch {
    return null;
  }
}

/** The strategy for this deployment. */
export function activeStrategyName(): AuthStrategyName {
  return strategyNameFor(runtimeMode, runtimeFlags.mocks);
}

/** Phone + PIN sign-in through the active strategy. Throws AuthError. */
export async function signIn(phone: string, pin: string): Promise<LoginResult> {
  if (runtimeMode !== 'node') await probeServerMode();
  const name = activeStrategyName();
  const result = await getStrategy(name).login(phone, pin);
  sessionStore.set(result, name, { fresh: true });
  lockStore.unlock();
  return result;
}

/**
 * Lock-screen unlock: re-authenticate the SAME person with their PIN. The
 * session (and everything rendered under the lock) stays; only the tokens are
 * replaced. A strategy with its own unlock (local: POST /auth/unlock on the
 * locked session) uses it; the others re-run login for the stored phone.
 * Throws AuthError (wrong PIN → stays locked; 'session_ended' → full login).
 */
export async function unlock(pin: string): Promise<void> {
  const user = sessionStore.getUser();
  if (!user?.phone) throw new AuthError('unexpected', AUTH_COPY.unexpected);
  const name = sessionStore.getStrategy() ?? activeStrategyName();
  const strategy = getStrategy(name);
  const token = sessionStore.getToken();
  const result = strategy.unlock && token ? await strategy.unlock(pin, token) : await strategy.login(user.phone, pin);
  // Keep the richer stored identity if the strategy returned a thinner one.
  // (a strategy that only knows the phone falls back to it as the name — keep the real one).
  const knownName = result.user.name && result.user.name !== result.user.phone ? result.user.name : user.name;
  sessionStore.set(
    {
      tokens: result.tokens,
      user: { ...user, ...result.user, name: knownName, initial: knownName.trim().charAt(0) || user.initial, phone: result.user.phone || user.phone },
      memberships: result.memberships,
      idleLockSeconds: result.idleLockSeconds,
    },
    name,
  );
  lockStore.unlock();
}

/**
 * Start a node session from a session erp-core issued outside /auth/login —
 * the first-run owner bootstrap (POST /v1/erp/auth/bootstrap answers the same
 * SessionResponse as login). Only the local strategy issues these.
 */
export function adoptLocalSession(data: LocalSessionResponse, phone: string): LoginResult {
  const result = toLoginResult(data, phone);
  runtimeMode = 'node';
  sessionStore.set(result, 'local', { fresh: true });
  lockStore.unlock();
  return result;
}

let refreshing: Promise<boolean> | null = null;

/** One silent renewal, coalesced across concurrent 401s. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const name = sessionStore.getStrategy();
  if (!name || !sessionStore.isAuthenticated()) return Promise.resolve(false);
  refreshing = (async () => {
    try {
      const r = await getStrategy(name).refresh(sessionStore.getRefreshToken());
      if (!r) return false;
      const prev = sessionStore.getUser();
      sessionStore.set({ tokens: r.tokens, user: prev ? { ...prev, ...pickDefined(r.user) } : r.user }, name);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function pickDefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== '')) as Partial<T>;
}

/** Full sign-out (switch user / explicit logout): server session + local clear. */
export async function signOut(): Promise<void> {
  const name = sessionStore.getStrategy();
  if (name) {
    try {
      await getStrategy(name).logout();
    } catch {
      /* best effort */
    }
  }
  sessionStore.clear();
  lockStore.unlock();
}
